import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthSessionExpiredError, useAuthSession } from '@app/context/auth-session';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { buildLoginRedirectPath } from '@app/utils/auth-redirect';
import { cloneHeadersWithAuth, resolveBasePath } from '@app/utils/api';
import { useConfig } from '@config/provider';

type ConnectState = 'loading' | 'success' | 'error' | 'unauthorized';
type ConnectProvider = 'slack' | 'teams';

type ConnectTokenClaims = { workspaceId?: string };

const connectProvider = (value: string | null, fallback?: ConnectProvider): ConnectProvider | null => {
  const provider = (value ?? fallback ?? '').trim().toLowerCase();
  return provider === 'slack' || provider === 'teams' ? provider : null;
};

const decodeConnectToken = (token: string): ConnectTokenClaims | null => {
  const parts = token.trim().split('.');
  // Support both the existing payload.signature format and standard JWTs.
  const payload = parts.length === 2 ? parts[0] : parts[1];
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(window.atob(padded)) as ConnectTokenClaims;
  } catch {
    return null;
  }
};

const providerLabel = (provider: ConnectProvider) => (provider === 'slack' ? 'Slack' : 'Teams');

export const IntegrationConnectPage = ({ defaultProvider }: { defaultProvider?: ConnectProvider }) => {
  const { apiBaseUrl } = useConfig();
  const basePath = useMemo(() => resolveBasePath(apiBaseUrl), [apiBaseUrl]);
  const { tokens, user, loading, switchOrganization } = useAuthSession();
  const { fetchWithAuthRetry } = useAuthFetch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const provider = connectProvider(searchParams.get('provider'), defaultProvider);
  const providerName = provider ? providerLabel(provider) : 'integration';
  const [state, setState] = useState<ConnectState>('loading');
  const [message, setMessage] = useState(`Connecting your ${providerName} account to REAP…`);
  const orgSwitchIntentRef = useRef<string | null>(null);

  const orgId = searchParams.get('orgId')?.trim() ?? '';
  const token = searchParams.get('token')?.trim() ?? '';
  const workspaceId = useMemo(() => decodeConnectToken(token)?.workspaceId?.trim() ?? '', [token]);
  const activeOrgId = useMemo(() => {
    const memberships = user?.memberships ?? [];
    return user?.activeOrg?.orgId?.trim() ?? memberships[0]?.orgId?.trim() ?? '';
  }, [user]);
  const integrationsPath = `/app/integrations?orgId=${encodeURIComponent(orgId)}`;
  const loginRedirectPath = useMemo(() => buildLoginRedirectPath(location, { orgId }), [location, orgId]);

  const returnToProvider = () => {
    if (provider === 'slack') {
      window.location.assign(workspaceId ? `https://app.slack.com/client/${encodeURIComponent(workspaceId)}` : 'https://app.slack.com/client');
      return;
    }
    window.location.assign('https://teams.microsoft.com');
  };

  useEffect(() => {
    if (!tokens && !loading) navigate(loginRedirectPath, { replace: true });
  }, [loading, loginRedirectPath, navigate, tokens]);

  useEffect(() => {
    if (!tokens || loading || !user) return;
    if (!provider || !orgId || !token) {
      setState('error');
      setMessage('This integration connect link is incomplete or invalid.');
      return;
    }
    let cancelled = false;
    const completeLink = async () => {
      try {
        if (activeOrgId && activeOrgId !== orgId) {
          if (!tokens.refreshToken) {
            navigate(loginRedirectPath, { replace: true });
            return;
          }
          if (orgSwitchIntentRef.current === orgId) return;
          orgSwitchIntentRef.current = orgId;
          setState('loading');
          setMessage('Switching to the requested REAP organization…');
          await switchOrganization(orgId);
          if (!cancelled) setMessage(`Connecting your ${providerLabel(provider)} account to REAP…`);
          return;
        }
        orgSwitchIntentRef.current = null;
        const headers = await cloneHeadersWithAuth({ Accept: 'application/json', 'Content-Type': 'application/json' });
        const response = await fetchWithAuthRetry(`${basePath}/orgs/${encodeURIComponent(orgId)}/integrations/${provider}/link/complete`, {
          method: 'POST', headers, body: JSON.stringify({ token })
        });
        if (!response.ok) {
          const detail = await response.text();
          if (response.status === 403) throw new Error(detail || 'You do not have access to this REAP organization.');
          throw new Error(detail || `${providerLabel(provider)} account linking failed.`);
        }
        if (!cancelled) {
          setState('success');
          setMessage(`Your ${providerLabel(provider)} account is now connected to REAP. You can return to ${providerLabel(provider)}.`);
        }
      } catch (error) {
        if (cancelled) return;
        orgSwitchIntentRef.current = null;
        if (error instanceof AuthSessionExpiredError) { navigate(loginRedirectPath, { replace: true }); return; }
        const detail = error instanceof Error ? error.message : `${providerName} account linking failed.`;
        if (detail.toLowerCase().includes('do not have access')) {
          setState('unauthorized'); setMessage('You signed in successfully, but you do not currently have access to this REAP organization.'); return;
        }
        setState('error'); setMessage(detail);
      }
    };
    void completeLink();
    return () => { cancelled = true; };
  }, [activeOrgId, basePath, fetchWithAuthRetry, loading, loginRedirectPath, navigate, orgId, provider, providerName, switchOrganization, token, tokens, user]);

  return (
    <main className="auth-layout">
      <section className="auth-card">
        <header className="auth-card__header"><h1 className="auth-title">{providerName} account connection</h1><p className="auth-subtitle">{message}</p></header>
        <div className="auth-inline-panel">
          {state === 'loading' && <p>Waiting for your REAP session…</p>}
          {state === 'success' && <><p className="auth-note">You can return to {providerName} now, or stay in REAP to review your integrations.</p><div className="auth-inline-actions"><button type="button" className="auth-primary" onClick={returnToProvider}>Return to {providerName}</button><button type="button" className="auth-secondary" onClick={() => navigate(integrationsPath)}>Open REAP</button></div></>}
          {state === 'unauthorized' && <div className="auth-inline-actions"><button type="button" className="auth-secondary" onClick={() => navigate(integrationsPath)}>Open REAP</button></div>}
          {state === 'error' && <div className="auth-inline-actions"><button type="button" className="auth-secondary" onClick={() => navigate('/login')}>Sign in again</button></div>}
        </div>
      </section>
    </main>
  );
};

export default IntegrationConnectPage;
